import {
  LookupCellData,
  getUserVisibleIndexValue,
  getWebPath,
  isFormulaType,
} from '@doss/common-all'
import { KeyboardReturn } from '@mui/icons-material'
import { Backdrop, Stack, Typography } from '@mui/material'
import { all as promiseAll, delay as promiseDelay } from 'bluebird'
import { Formik } from 'formik'
import { get, orderBy } from 'lodash'
import { Primitive } from 'type-fest'
import {
  BreadcrumbObjectType,
  CreateTableRowMutation,
  DataType,
  GetSettingsPageTableDetailsDocument,
  useAddRecordGetTableDetailsQuery,
  useCreateTableRowMutation,
  useDeleteDocumentMutation,
} from '~/common/generated/apollo-operations'
import { useTrackDocumentIds } from '~/common/hooks/useTrackDocumentIds'
import { getCreateTableRowCells, getRecordValidationSchema } from '~/common/utils/record'
import { getRowNameColumnId } from '~/common/utils/table'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { Button } from '~/components/ui/atoms/button/Button'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { AddRecordForm } from './AddRecordForm'

import { FetchResult } from '@apollo/client'
import { createPortal } from 'react-dom'
import { TbColumns } from 'react-icons/tb'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { JSONObject } from '~/common/scalars'
import { getAddRecordInitialValues } from '~/common/utils/formik'
import { controlKey } from '~/common/utils/keymap'
import { RecordDetailTableCellUser } from '../types'

export type LookupCellValue = {
  id: string
  userVisibleValue: string | null
  row: { id: string }
  data: JSONObject | null
  documents: { id: string }[]
}

export function isLookupCellValue(value: unknown): value is LookupCellValue {
  return typeof value === 'object' && value !== null && 'userVisibleValue' in value
}

export type CellValue =
  | Primitive
  | string[]
  | LookupCellValue[]
  | LookupCellData
  | RecordDetailTableCellUser[]

interface AddRecordDialogProps {
  open: boolean
  onClose: () => void
  tableId: string
  parentRowId?: string
}

// Dialog to add a new record to a table
function AddRecordDialog({
  open,
  onClose,
  tableId,
  parentRowId,
}: AddRecordDialogProps): JSX.Element | null {
  const { t } = useTypedTranslation()
  const { documentIds, addDocumentId, removeDocumentId } = useTrackDocumentIds()
  const { permissionsManager } = useGlobalContext()
  const snackbar = useDossSnackbar()
  const { data } = useAddRecordGetTableDetailsQuery({
    variables: {
      tableId,
    },
  })
  const [createRow] = useCreateTableRowMutation({
    refetchQueries: [
      {
        query: GetSettingsPageTableDetailsDocument,
        variables: { tableId },
      },
    ],
  })

  const [deleteDocument] = useDeleteDocumentMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }

      const normalizedId = cache.identify(data.deleteDocument)
      cache.evict({ id: normalizedId })
      cache.gc()
    },
  })

  if (!data) {
    return null
  }

  const orderedColumns = orderBy(data.table.columns, (column) => column.order, 'asc')
  const editableOrderedColumns = orderedColumns.filter((column) =>
    permissionsManager.canEditCell(tableId, column.id),
  )
  const initialValues = getAddRecordInitialValues(orderedColumns)
  const childTableIds = data.table.childTables.map((childTable) => childTable.id)

  const handleSubmit = async (values: Record<string, CellValue>) => {
    const updateCells = getCreateTableRowCells(values, orderedColumns, (columnId) =>
      permissionsManager.canEditCell(tableId, columnId),
    )

    await createRow({
      variables: {
        input: {
          tableId,
          cells: updateCells,
          parentRowId,
        },
      },
    })
      .then(async (result: FetchResult<CreateTableRowMutation>) => {
        await promiseDelay(400)
        if (result.data) {
          let linkToRecord: string | undefined
          if (!parentRowId) {
            linkToRecord = getWebPath({
              objectType: BreadcrumbObjectType.TABLE_ROW,
              objectId: result.data.createTableRow.id,
            })
          }
          snackbar.showSuccess(
            <Stack alignItems="center" direction="row" spacing={1}>
              <Typography>{t('record.create_success')}</Typography>
              <TbColumns fontSize="20px" />
            </Stack>,
            linkToRecord,
          )
        }
        onClose()
      })
      .catch((error) => {
        snackbar.showError(t('record.create_failure', { errorMessage: error.message }))
      })
  }

  const getRowName = (values: Record<string, CellValue>) => {
    const rowNameColumnId = getRowNameColumnId(editableOrderedColumns)
    const column = data.table.columns.find((column) => column.id === rowNameColumnId)
    let value = rowNameColumnId ? get(values, rowNameColumnId, null) : null
    if (value) {
      const isIdColumn = column?.type === DataType.ID
      if (isIdColumn && (typeof value === 'string' || typeof value === 'number')) {
        value = getUserVisibleIndexValue(value, column.indexPrefix, column.indexSuffix)
      } else if (
        column?.type === DataType.LOOKUP &&
        Array.isArray(value) &&
        value.length > 0 &&
        isLookupCellValue(value[0])
      ) {
        value = value[0].userVisibleValue ?? ''
      }
      return value ? `${t('record.add_record')}: ${value.toString()}` : t('record.add_record')
    }
    return t('record.add_record')
  }

  const handleCloseWithoutSaving = async () => {
    // If the user uploaded files, delete the associated documents if they close the dialog
    // without saving
    await promiseAll(documentIds.map((documentId) => deleteDocument({ variables: { documentId } })))
    onClose()
  }

  const handleBackdropCloseWithoutSaving = async () => {
    if (window.confirm(t('common.confirm_leave'))) {
      await promiseAll(
        documentIds.map((documentId) => deleteDocument({ variables: { documentId } })),
      )
      onClose()
    }
  }

  const validationSchema = getRecordValidationSchema(orderedColumns)

  // DisablePortal is true because we require the add record form to be under the DOM hierarchy of
  // the form.  This, however, causes the backdrop to not appear over the entire screen when the
  // modal is open for creating a child table record. To prevent this, we are creating a portal
  // that wraps around the form and a separate backdrop component and we are hiding the modal's
  // own backdrop.  The z-index of the backdrop is set to the default MUI modal z-index - 1 so it
  // covers the top nagivation bars
  return createPortal(
    <>
      <Backdrop onClick={handleBackdropCloseWithoutSaving} open={open} style={{ zIndex: 1299 }} />
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ values, isValid, isSubmitting, handleSubmit }) => {
          const hasValues = Object.values(values).some((value) => value !== null)
          const onlyHasGeneratedColumns = data.table.columns.every((column) => {
            return (
              isFormulaType(column.type) ||
              (column.type === DataType.ID && column.indexAutogenerate)
            )
          })

          return (
            <form onSubmit={handleSubmit}>
              <Modal
                actions={
                  <Button
                    disabled={(!hasValues && !onlyHasGeneratedColumns) || !isValid || isSubmitting}
                    endIcon={controlKey(<KeyboardReturn />)}
                    size="small"
                    type="submit"
                    variant="secondary"
                  >
                    {t('common.actions.save')}
                  </Button>
                }
                disablePortal
                fullWidth
                hideBackdrop
                onBackdropClose={handleBackdropCloseWithoutSaving}
                onClose={handleCloseWithoutSaving}
                open={open}
                sx={{
                  '& .MuiPaper-root': {
                    overflowY: 'hidden',
                  },
                }}
                title={getRowName(values)}
              >
                <AddRecordForm
                  addDocumentId={addDocumentId}
                  childTableIds={childTableIds}
                  handleCloseWithoutSaving={handleCloseWithoutSaving}
                  orderedColumns={orderedColumns}
                  removeDocumentId={removeDocumentId}
                  tableId={tableId}
                />
              </Modal>
            </form>
          )
        }}
      </Formik>
    </>,
    document.body,
  )
}

export default AddRecordDialog
